/**
 * The seating-PDF export dialog's form model and its wire mapping.
 *
 * `PSeatingExport.tsx` is a thin view over `SeatingExportForm`; everything
 * that decides WHAT goes on the wire lives here so it can be pinned without
 * rendering a dialog: the two presets, the content gate, the blocked reason,
 * and `buildSeatingExportParams`, the single place that turns the form into
 * `OptionalParamsBackgroundJobs`.
 *
 * The literal unions are the FE half of the seating-export wire contract
 * (main-consumer `src/lib/seating-export-options.ts`, main-backend
 * `SeatingExportValidation`). `__tests__/seating-export-options-parity.json`
 * is the shared fixture all three repos assert against — change an enum here
 * and the fixture, and the other two copies, in the same change set.
 */
import type { OptionalParamsBackgroundJobs } from "@/types/OptionalParamsBackgroundJobs";

export const SEATING_PAPER_SIZES = ["A4", "A3"] as const;
export type SeatingPaperSize = (typeof SEATING_PAPER_SIZES)[number];

export const SEATING_ORIENTATIONS = ["auto", "portrait", "landscape"] as const;
export type SeatingOrientation = (typeof SEATING_ORIENTATIONS)[number];

export const SEATING_MODES = ["print", "planning"] as const;
export type SeatingMode = (typeof SEATING_MODES)[number];

export const SEATING_DETAIL_PAGES = ["none", "zones", "tables"] as const;
export type SeatingDetailPages = (typeof SEATING_DETAIL_PAGES)[number];

/** What fills a placed chair on the plan (seats.io Teil 2, Zug 2): its status,
 *  or its guest's option of the room's colour field. */
export const SEATING_COLOUR_BY = ["status", "category"] as const;
export type SeatingColourBy = (typeof SEATING_COLOUR_BY)[number];

// "unassigned" is deliberately NOT in this list: the "include unassigned
// seats" checkbox in the Content group is its single control and rides into
// `seatStatusFilter` from there. Listing it in both places made the checkbox
// inert — the renderer ANDs the two gates, so with the chip unticked (the
// default) the flag could never surface a row.
/**
 * The cockpit's status filter. Exported because five hand-written copies of
 * this list exist as `FE_DEFAULT_FILTER` in the renderer suites of
 * main-consumer, main-backend and e2e_main — they cannot import across repo
 * boundaries, so they name this constant in a comment instead. Change it here
 * and those copies go stale silently.
 */
export const ALL_STATUSES = [
  "assigned",
  "tentative",
  "reserved",
  "checkedIn",
] as const;
export type SeatStatusKey = (typeof ALL_STATUSES)[number];

/** Wire status carried by the unassigned opt-in (never a chip, see above). */
export const UNASSIGNED_STATUS = "unassigned";

/** Hard cap on `guestFields`, mirrored by the FE validator and both servers. */
export const SEATING_GUEST_FIELDS_MAX = 20;

/**
 * Built-in guest columns the picker offers, in picker order. The consumer's
 * `ALLOWED_SEATING_PDF_GUEST_FIELDS` is the authority; this is the subset with
 * a place in the dialog. Contact data is a separate group so it is never
 * ticked by accident — e-mail and phone numbers on a printed door list are a
 * different decision than a company name.
 */
export const SEATING_GUEST_FIELD_GROUPS = {
  guest: [
    "title",
    "gender",
    "company",
    "jobTitle",
    "language",
    "state",
    "acceptanceState",
    "checkedIn",
    "guestType",
    "numCompanions",
    "companions",
    "mainGuestName",
  ],
  contact: ["email", "phone", "mobile"],
} as const;

/** Prefix a custom field's name carries on the wire (`additionalFields.<name>`). */
export const CUSTOM_FIELD_PREFIX = "additionalFields.";

/**
 * Which rooms the export draws: every room, or the ticked subset. The
 * "current room" option of the first version is gone — the room the canvas
 * shows is the list's FIRST, pre-ticked entry instead (W1-15), which sends
 * the same `roomIds=[active]` the old radio sent.
 */
export type RoomMode = "all" | "select";

export interface SeatingExportForm {
  mode: SeatingMode;
  roomMode: RoomMode;
  selectedRoomIds: ReadonlySet<string>;
  includeFloorPlan: boolean;
  includeGuestList: boolean;
  includeGuestIndex: boolean;
  includeStatsSummary: boolean;
  includeColourLegend: boolean;
  /** Not part of a preset: a preset click keeps it. Print mode, and an export
   *  whose rooms have no readable colour field, send "status" whatever it
   *  holds (`buildSeatingExportParams`). */
  colourBy: SeatingColourBy;
  includeUnassignedSeats: boolean;
  detailPages: SeatingDetailPages;
  /** Subset of `ALL_STATUSES`; the unassigned opt-in is NOT a member. */
  statusFilter: ReadonlySet<string>;
  paperSize: SeatingPaperSize;
  orientation: SeatingOrientation;
  posterTiling: boolean;
  /** Wire keys: bare built-in names, `additionalFields.<name>` for custom. */
  guestFields: readonly string[];
}

/** The slice of the form a preset click rewrites. Paper, orientation, poster
 *  tiling, the guest columns and the room choice survive a preset switch. */
export type SeatingPreset = Pick<
  SeatingExportForm,
  | "includeFloorPlan"
  | "includeGuestList"
  | "includeGuestIndex"
  | "includeStatsSummary"
  | "includeColourLegend"
  | "includeUnassignedSeats"
  | "detailPages"
  | "statusFilter"
>;

/**
 * Preset → flags. Dialog-only: the consumer derives NOTHING from `seatingMode`
 * beyond its own rendering (print = no status colours / status column), so a
 * preset is exactly the flags it sends. Fresh objects on every call — a shared
 * Set would let one dialog's tick leak into the next one's defaults.
 */
export function presetDefaults(mode: SeatingMode): SeatingPreset {
  if (mode === "print") {
    return {
      includeFloorPlan: true,
      includeGuestList: true,
      includeGuestIndex: true,
      includeStatsSummary: false,
      includeColourLegend: false,
      includeUnassignedSeats: false,
      detailPages: "zones",
      statusFilter: new Set<string>(ALL_STATUSES),
    };
  }
  return {
    includeFloorPlan: true,
    includeGuestList: true,
    includeGuestIndex: true,
    includeStatsSummary: true,
    includeColourLegend: true,
    includeUnassignedSeats: true,
    detailPages: "none",
    statusFilter: new Set<string>(ALL_STATUSES),
  };
}

/** A complete form in its initial state: the print preset plus the survivors'
 *  defaults. `activeRoomId` — the room the canvas shows — arrives pre-ticked;
 *  without one the selection is empty and the form is BLOCKED
 *  (`exportNoRoomsSelected`), never a silent every-room export. */
export function initialSeatingExportForm(
  ctx: { activeRoomId?: string } = {},
): SeatingExportForm {
  return {
    mode: "print",
    roomMode: "select",
    selectedRoomIds: new Set<string>(ctx.activeRoomId ? [ctx.activeRoomId] : []),
    ...presetDefaults("print"),
    colourBy: "status",
    paperSize: "A4",
    orientation: "auto",
    posterTiling: false,
    guestFields: [],
  };
}

/**
 * The single source of truth for what the renderer keeps. The unassigned
 * opt-in rides IN here, so one tick is enough — no second chip to hunt for.
 * Order follows `ALL_STATUSES` so the payload is deterministic regardless of
 * the order the user ticked the chips in.
 */
export function effectiveStatusFilter(form: SeatingExportForm): string[] {
  const out: string[] = ALL_STATUSES.filter((s) => form.statusFilter.has(s));
  if (form.includeUnassignedSeats) out.push(UNASSIGNED_STATUS);
  return out;
}

/** True when at least one section would print. Detail pages count: a zones-
 *  only export (print preset with every checkbox off) is still a document. */
export function hasAnyContent(form: SeatingExportForm): boolean {
  return (
    form.includeFloorPlan ||
    form.includeGuestList ||
    form.includeStatsSummary ||
    form.includeGuestIndex ||
    form.detailPages !== "none"
  );
}

/** `availableRooms` is how many rooms the event HAS: with none, "all rooms"
 *  is an export of nothing and must not pass the gate either (audit r1). */
export function hasRooms(form: SeatingExportForm, availableRooms?: number): boolean {
  if (availableRooms === 0) return false;
  return form.roomMode !== "select" || form.selectedRoomIds.size > 0;
}

export type SeatingExportBlockedReasonKey =
  | "exportNoRoomsSelected"
  | "exportNoStatusSelected"
  | "exportNoContentSelected";

/**
 * Why the Export button is dead, as a `seating.*` key — or `null` when it is
 * not. Every gate has a sentence, in the dialog's own order (rooms, status,
 * content): a greyed button that says nothing is worse than one that refuses.
 */
export function blockedReasonKey(
  form: SeatingExportForm,
  ctx: { availableRooms?: number } = {},
): SeatingExportBlockedReasonKey | null {
  if (!hasRooms(form, ctx.availableRooms)) return "exportNoRoomsSelected";
  if (effectiveStatusFilter(form).length === 0) return "exportNoStatusSelected";
  if (!hasAnyContent(form)) return "exportNoContentSelected";
  return null;
}

export type ColourByBlockedReasonKey =
  | "exportColourByPrintBlocked"
  | "seatColor.hiddenForRole"
  | "exportColourByNoField"
  | "exportColourByNoPlan";

/**
 * Does this export draw a plan page — the overview (with its poster sheets)
 * or the zone pages? Only those colour chairs by category; table cards stay
 * in status colours. EXACTLY main-consumer's rule (`resolveRoomColourFields`,
 * src/handlers/export-pdf-chunk.ts: `includeFloorPlan || detailPages ===
 * 'zones'`) — audit r1 L3-9: a category tick on a tables-only export did
 * nothing, silently.
 */
export function exportDrawsPlan(form: Pick<SeatingExportForm, "includeFloorPlan" | "detailPages">): boolean {
  return form.includeFloorPlan || form.detailPages === "zones";
}

/**
 * Why "Colour chairs by category" is greyed, as a `seating.*` key — or null
 * when it may be chosen. Print first: the printed plan always shows the
 * status glyphs. Then the colour field: none readable in the export —
 * `categoryHidden` when the reason is that the room's field is hidden from
 * this role (audit r1 UX-3), else no room has one. Last, the pages: no plan
 * page is drawn (`exportDrawsPlan`), which the operator can change right here.
 */
export function colourByBlockedReasonKey(
  form: SeatingExportForm,
  ctx: { categoryAvailable: boolean; categoryHidden?: boolean },
): ColourByBlockedReasonKey | null {
  if (form.mode === "print") return "exportColourByPrintBlocked";
  if (!ctx.categoryAvailable) return ctx.categoryHidden === true ? "seatColor.hiddenForRole" : "exportColourByNoField";
  if (!exportDrawsPlan(form)) return "exportColourByNoPlan";
  return null;
}

export interface SeatingExportContext {
  /** The room the canvas shows; `undefined` when none is open. */
  activeRoomId: string | undefined;
  /** BCP-47 tag of the operator's UI language (server-side date formatting). */
  locale: string;
  /** `buildExportPdfLabels(t)` — the renderer's localized label map. */
  exportLabels: Record<string, string>;
  /** Header labels for the chosen `guestFields`, keyed by wire key. Sent only
   *  when a guest column is chosen; an empty map never travels. */
  columnLabels?: Record<string, string>;
  /** Editable download name; blank → the consumer's default. */
  exportFileName?: string;
  /** A room in the export has a colour field this account can read
   *  (seats.io Teil 2, Zug 2). Absent = no: `colourBy` travels as "status". */
  colourByCategoryAvailable?: boolean;
}

/**
 * Form → job body. Every seating option travels on every submit, including the
 * ones at their default, so the two servers validate what the dialog decided
 * rather than fall back on their own defaults. Never sends
 * `includeFloorPlanLegend`: the key is deprecated on the wire and the renderer
 * ignores it.
 */
export function buildSeatingExportParams(
  form: SeatingExportForm,
  ctx: SeatingExportContext,
): OptionalParamsBackgroundJobs {
  const params: OptionalParamsBackgroundJobs = {
    seatStatusFilter: effectiveStatusFilter(form),
    includeFloorPlan: form.includeFloorPlan,
    includeGuestList: form.includeGuestList,
    includeStatsSummary: form.includeStatsSummary,
    includeUnassignedSeats: form.includeUnassignedSeats,
    includeGuestIndex: form.includeGuestIndex,
    includeColourLegend: form.includeColourLegend,
    // Every submit, like every other option. "category" only where it can
    // mean something — never in print mode (status glyphs), never without a
    // readable colour field in the export, never without a plan page (the
    // dialog greys it, see `colourByBlockedReasonKey`).
    colourBy: colourByBlockedReasonKey(form, { categoryAvailable: ctx.colourByCategoryAvailable === true }) === null
      ? form.colourBy
      : "status",
    detailPages: form.detailPages,
    paperSize: form.paperSize,
    orientation: form.orientation,
    seatingMode: form.mode,
    posterTiling: form.posterTiling,
    // Localize the server-rendered PDF in the operator's UI language.
    language: ctx.locale,
    exportLabels: ctx.exportLabels,
  };

  // "all" sends no roomIds; "select" sends the ticked set — by default the
  // active room alone, which is byte-for-byte what the old "current" mode
  // sent. `ctx.activeRoomId` no longer decides anything here: the form
  // carries the room (see `initialSeatingExportForm`).
  if (form.roomMode === "select") {
    params.roomIds = Array.from(form.selectedRoomIds);
  }

  // `guestFields` only when non-empty (absent ⇒ no guest join on the worker),
  // and `columnLabels` only alongside it — a label map for zero columns is
  // noise the validator would still have to read.
  if (form.guestFields.length > 0) {
    params.guestFields = Array.from(form.guestFields);
    if (ctx.columnLabels && Object.keys(ctx.columnLabels).length > 0) {
      params.columnLabels = ctx.columnLabels;
    }
  }

  const fileName = ctx.exportFileName?.trim();
  if (fileName) params.exportFileName = fileName;

  return params;
}
